import {
  CALENDAR_STATUSES,
  CalendarCaptain,
  CalendarData,
  CalendarDay,
  CalendarMonth,
  CalendarSetup,
  CalendarStatus,
  DEFAULT_NINE_SETUP_STATUS,
  NINE_SETUP_STATUSES,
  NineSetup,
  NineSetupCalendarCaptainDTO,
  NineSetupCalendarDayDTO,
  NineSetupCalendarMonthDTO,
  NineSetupCalendarReportDTO,
  NineSetupCalendarSetupDTO,
  NineSetupStatus,
} from "../../../interfaces/NineSetupObject";
import { PaymentStatus } from "../../../interfaces/OnboardingObject";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";

// The nine_setup API is camelCase (new convention), so these converters only
// null-guard, coerce numbers and normalise the status string — a partial or
// slightly-off backend response can never crash the UI or render NaN.
//
// Design rule shared by every helper below: ABSENT means null, not "" or 0.
// The row type is nullable exactly where the DDL is, so a component can tell
// "no fee recorded" (null → render an em dash) from "₹0 fee" (0 → render ₹0).

/**
 * Flip to `true` if the backend really sends money in paise, as the SQL column
 * comments suggest. Everything else in the UI (OnboardingObject.onboardingFee,
 * PendingBusiness.finalFee) is already in rupees, so paise values would render
 * 100× too large — this constant is the single place that reconciles the two.
 *
 * SETTLED 2026-08-01: the OpenAPI spec types `onboardingFee` and `curatorFee` as
 * `integer/int32` on NineSetupDTO — whole rupees, matching the rest of the UI.
 * (Contrast SubscriptionPlans.amountPaise, which names its unit when it means
 * paise.) Stays `false`.
 */
// Annotated `boolean` (not left to infer the literal `false`) so flipping it is
// a one-word change and TypeScript doesn't narrow the branch below out of
// existence in the meantime.
const NINE_SETUP_FEES_ARE_IN_PAISE: boolean = false;

const toRequiredString = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value);

/** Null when absent or unrecognised — never guess a payment state. */
const toPaymentStatus = (value: unknown): PaymentStatus | null =>
  value === "PAID" || value === "UNPAID" || value === "NOT_REQUIRED" ? value : null;

/** Absent, null or empty-string → null. Guards against the backend emitting ""
 *  for an unset TEXT column, which would otherwise render as a blank field the
 *  user can't distinguish from a real empty value. */
const toNullableString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value);
  return text === "" ? null : text;
};

/** Like toNullableString, but keeps "the server didn't send this key" (undefined)
 *  apart from "sent, and empty" (null) — for fields where that difference
 *  changes what the UI may conclude (see NineSetup.commissionCreditedAt). */
const toOptionalNullableString = (
  raw: Record<string, unknown>,
  key: string
): string | null | undefined =>
  raw && key in raw ? toNullableString(raw[key]) : undefined;

/** Absent/null/empty/unparseable → null. NUMERIC columns arrive as either a JS
 *  number or a string ("12.3456789") depending on the PG driver, so String and
 *  number inputs must both work. */
const toNullableNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Money column → the units the UI renders in (see NINE_SETUP_FEES_ARE_IN_PAISE). */
const toFee = (value: unknown): number | null => {
  const amount = toNullableNumber(value);
  if (amount === null) return null;
  return NINE_SETUP_FEES_ARE_IN_PAISE ? amount / 100 : amount;
};

/** Defaults to `fallback` when the key is absent — used for is_media_required,
 *  whose DB default is TRUE. Anything other than a real boolean (including the
 *  strings "true"/"false" some drivers emit) is resolved explicitly. */
const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
};

/** Trim + lower-case, then check against an allowlist. Shared by every status
 *  normaliser below so the casing rule lives in one place. */
const matchStatus = <T extends string>(
  value: unknown,
  allowed: readonly T[]
): T | null => {
  if (typeof value !== "string") return null;
  const normalised = value.trim().toLowerCase();
  return (allowed as readonly string[]).includes(normalised)
    ? (normalised as T)
    : null;
};

/**
 * Normalises the PG enum value. The stored form is lower_snake
 * ('media_collected'), but a backend that upper-cases its enums on the way out
 * ('MEDIA_COLLECTED', as ApprovalStatus does) must not silently degrade every
 * row — so the input is trimmed and lower-cased before the allowlist check.
 *
 * Unknown values fall back to 'targeted' rather than throwing: a status the UI
 * doesn't recognise yet (new enum value shipped backend-first) should show the
 * row at the start of the funnel, not blank the whole list. Follows the same
 * fail-soft precedent as onboardingConvertor's toApprovalStatus.
 */
const toNineSetupStatus = (value: unknown): NineSetupStatus => {
  if (typeof value !== "string") return DEFAULT_NINE_SETUP_STATUS;
  const matched = matchStatus(value, NINE_SETUP_STATUSES);
  if (matched) return matched;
  const normalised = value.trim().toLowerCase();

  // Fail soft, but NOT silently. Falling back to 'targeted' keeps the list
  // rendering, yet it also rewrites a row's meaning on screen — a `paid` row
  // shown as Targeted will be offered the sales flow again. Four statuses were
  // retired client-side on 2026-08-02 (see NineSetupObject); if the database
  // still holds them this is where it shows up, and it must be caught in dev
  // rather than discovered by a captain.
  // eslint-disable-next-line no-console
  console.warn(
    `[nineSetup] Unknown status "${normalised}" from the API — falling back to "${DEFAULT_NINE_SETUP_STATUS}". ` +
    `The PG enum and NINE_SETUP_STATUSES have diverged.`
  );
  return DEFAULT_NINE_SETUP_STATUS;
};

/** Calendar-report variant of toNineSetupStatus: same normalisation, but it
 *  also accepts the retired historical statuses and yields null (not
 *  'targeted') for anything unknown — a read-only report must not relabel a
 *  row it doesn't understand. */
const toCalendarStatus = (value: unknown): CalendarStatus | null =>
  matchStatus(value, CALENDAR_STATUSES);

/**
 * One `nine_setup` row → NineSetup.
 *
 * The primary key is read as `setupId ?? id`: the column is literally `id`, but
 * a backend following the repo's own naming (pendingId, captainId) may well
 * expose it as `setupId`. Accepting both costs nothing and removes a whole
 * class of "everything renders but nothing updates" bugs, since the slice keys
 * its upsert on this value.
 */
export function convertNineSetup(raw: Record<string, unknown>): NineSetup {
  return {
    setupId: toRequiredString(raw?.setupId ?? raw?.id),

    // Business
    businessName: toRequiredString(raw?.businessName),
    customerMobile: toRequiredString(raw?.customerMobile),
    location: toNullableString(raw?.location),
    latitude: toNullableNumber(raw?.latitude),
    longitude: toNullableNumber(raw?.longitude),

    // Captain
    captainId: toRequiredString(raw?.captainId),
    captainMobile: toNullableString(raw?.captainMobile),

    // Intake
    googleFormLink: toNullableString(raw?.googleFormLink),
    // Wire name is `mediaRequired` (NineSetupDTO); `isMediaRequired` is accepted
    // first in case the backend ever adopts the UI's spelling. `??` not `||`, so
    // a genuine `false` is preserved rather than falling through to the alias.
    // DB default is TRUE — an omitted key must not silently disable the media
    // step for a row that actually requires it.
    isMediaRequired: toBoolean(raw?.isMediaRequired ?? raw?.mediaRequired, true),

    // State
    status: toNineSetupStatus(raw?.status),
    statusNote: toNullableString(raw?.statusNote),

    // Relations
    curatorId: toNullableString(raw?.curatorId),
    // Absent from the wire today — null until the backend ships it. See the
    // field's note on NineSetup.
    curatorMobile: toNullableString(raw?.curatorMobile),
    businessId: toNullableString(raw?.businessId),

    // Money
    onboardingFee: toFee(raw?.onboardingFee),
    curatorFee: toFee(raw?.curatorFee),
    // Commissions are ₹ (same units as the captain-summary totals they add up
    // to), so they are NOT run through toFee's paise scaling.
    captainCommission: toNullableNumber(raw?.captainCommission),
    leadCommission: toNullableNumber(raw?.leadCommission),
    curatorCommission: toNullableNumber(raw?.curatorCommission),
    commissionCreditedAt: toOptionalNullableString(raw, "commissionCreditedAt"),
    leadCommissionCreditedAt: toOptionalNullableString(raw, "leadCommissionCreditedAt"),
    curatorCommissionCreditedAt: toOptionalNullableString(
      raw,
      "curatorCommissionCreditedAt"
    ),
    captainFixedCommission: toNullableNumber(raw?.captainFixedCommission),
    captainVariableCommission: toNullableNumber(raw?.captainVariableCommission),
    captainFixedCreditedAt: toOptionalNullableString(raw, "captainFixedCreditedAt"),
    curatorFixedCommission: toNullableNumber(raw?.curatorFixedCommission),
    curatorVariableCommission: toNullableNumber(raw?.curatorVariableCommission),
    curatorFixedCreditedAt: toOptionalNullableString(raw, "curatorFixedCreditedAt"),
    paymentStatus: toPaymentStatus(raw?.paymentStatus),
    captainName: toNullableString(raw?.captainName),
    curatorName: toNullableString(raw?.curatorName),
    targetDate: toNullableString(raw?.targetDate),
    enquiryNote: toNullableString(raw?.enquiryNote),
    enquiryTargetDate: toNullableString(raw?.enquiryTargetDate),
    enquiredAt: toNullableString(raw?.enquiredAt),

    // Media collection details
    imageCount: toNullableNumber(raw?.imageCount),
    pdfCount: toNullableNumber(raw?.pdfCount),
    pdfTotalPages: toNullableNumber(raw?.pdfTotalPages),
    mediaCollectedBy: toNullableString(raw?.mediaCollectedBy),

    // Lifecycle timestamps
    contactedAt: toNullableString(raw?.contactedAt),
    acceptedAt: toNullableString(raw?.acceptedAt),
    paidAt: toNullableString(raw?.paidAt),
    claimedAt: toNullableString(raw?.claimedAt),
    mediaCollectedAt: toNullableString(raw?.mediaCollectedAt),
    submittedAt: toNullableString(raw?.submittedAt),
    onboardedAt: toNullableString(raw?.onboardedAt),

    createdAt: toRequiredString(raw?.createdAt),
    updatedAt: toRequiredString(raw?.updatedAt),
  };
}

/** True when an object looks like a setup row rather than a wrapper — used to
 *  tell `contents: [row, row]` apart from `contents: [{ setups: [...] }]`.
 *  Probes the PK under both accepted spellings (see convertNineSetup). */
const looksLikeSetupRow = (candidate: Record<string, unknown> | undefined): boolean =>
  !!candidate && (candidate.setupId !== undefined || candidate.id !== undefined);

/**
 * `contents` of the list call → NineSetup[].
 *
 * Shape-tolerant for the same reason as convertPendingBusinessList: the backend
 * reuses legacy response builders, so `contents` arrives as any of —
 *   1. { setups: [...] }          — documented shape
 *   2. [ { setups: [...] } ]      — array-wrapped wrapper (legacy builder)
 *   3. [ {id, ...}, ... ]         — raw rows directly in contents
 * `nineSetups` is accepted as an alias for the wrapper key in shapes 1 and 2.
 *
 * Detection assumption: the PK is present on every row and is never a key on
 * the wrapper object, so probing contents[0] cleanly separates shape 3 from 2.
 */
export function convertNineSetupList(contents: unknown): NineSetup[] {
  if (Array.isArray(contents)) {
    const first = contents[0] as Record<string, unknown> | undefined;
    if (looksLikeSetupRow(first)) {
      // Shape 3: contents IS the row array.
      return contents.map((row) => convertNineSetup(row as Record<string, unknown>));
    }
    // Shape 2 falls through to coerceSingle below; an empty array yields [].
  }

  const wrapper = coerceSingle(contents);
  const rows = wrapper.setups ?? wrapper.nineSetups;
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => convertNineSetup(row as Record<string, unknown>));
}

/**
 * `contents` of the delete call → the id that was removed.
 *
 * A DELETE may legitimately return an empty body, the deleted row, or just
 * `{ id }`, so the caller passes the id it requested as `requestedId` and that
 * is used whenever the response carries nothing usable. The slice needs a
 * definite id to drop the row from state.
 */
export function convertDeletedNineSetupId(
  contents: unknown,
  requestedId: string
): string {
  const raw = coerceSingle(contents);
  const returned = raw.setupId ?? raw.id;
  return returned === undefined || returned === null || returned === ""
    ? requestedId
    : String(returned);
}

// ── Onboarding Calendar report ──────────────────────────────────────────────
// Mirrors NineSetupReportDTO from the OpenAPI spec (see NineSetupObject). One
// small converter per nesting level; no field aliases or fallback chains, so a
// backend rename shows up as missing data rather than being papered over.

/** Counts are int64 on the wire; absent/unparseable → 0. */
const toCount = (value: unknown): number => toNullableNumber(value) ?? 0;

/** Absent or non-array → []. */
const toList = <T>(value: T[] | undefined): T[] =>
  Array.isArray(value) ? value : [];

const convertCalendarCaptain = (raw: NineSetupCalendarCaptainDTO): CalendarCaptain => ({
  captainId: toRequiredString(raw.captainId),
  captainName: toRequiredString(raw.captainName),
  count: toCount(raw.count),
});

const convertCalendarSetup = (raw: NineSetupCalendarSetupDTO): CalendarSetup => ({
  nineSetupId: toRequiredString(raw.nineSetupId),
  captainId: toRequiredString(raw.captainId),
  captainName: toRequiredString(raw.captainName),
  businessId: toNullableString(raw.businessId),
  businessName: toRequiredString(raw.businessName),
  status: toCalendarStatus(raw.status),
  eventAt: toNullableString(raw.timestamp),
  createdAt: toNullableString(raw.createdAt),
  contactedAt: toNullableString(raw.contactedAt),
  acceptedAt: toNullableString(raw.acceptedAt),
  submittedAt: toNullableString(raw.submittedAt),
  approvedAt: toNullableString(raw.approvedAt),
  claimedAt: toNullableString(raw.claimedAt),
  mediaCollectedAt: toNullableString(raw.mediaCollectedAt),
  completedAt: toNullableString(raw.completedAt),
  onboardedAt: toNullableString(raw.onboardedAt),
  paidAt: toNullableString(raw.paidAt),
  rejectedAt: toNullableString(raw.rejectedAt),
  inProgressAt: toNullableString(raw.inProgressAt),
  blockedAt: toNullableString(raw.blockedAt),
  inReviewAt: toNullableString(raw.inReviewAt),
  welcomeKitPreparedAt: toNullableString(raw.welcomeKitPreparedAt),
  handedOverAt: toNullableString(raw.handedOverAt),
  curatorId: toNullableString(raw.curatorId),
  curatorName: toNullableString(raw.curatorName),
  curatorMobile: toNullableString(raw.curatorMobile),
  captainMobile: toNullableString(raw.captainMobile),
  customerMobile: toNullableString(raw.customerMobile),
  targetDate: toNullableString(raw.targetDate),
  enquiryNote: toNullableString(raw.enquiryNote),
  enquiryTargetDate: toNullableString(raw.enquiryTargetDate),
  enquiredAt: toNullableString(raw.enquiredAt),
});

const convertCalendarDay = (raw: NineSetupCalendarDayDTO): CalendarDay => ({
  date: toRequiredString(raw.date),
  total: toCount(raw.total),
  captains: toList(raw.captains).map(convertCalendarCaptain),
  setups: toList(raw.setups).map(convertCalendarSetup),
});

const convertCalendarMonth = (raw: NineSetupCalendarMonthDTO): CalendarMonth => ({
  month: toRequiredString(raw.month),
  total: toCount(raw.total),
  days: toList(raw.days).map(convertCalendarDay),
});

/** `contents` of the onboardings-by-day call → CalendarData. */
export function convertCalendarData(contents: unknown): CalendarData {
  // The single wire-boundary cast: coerceSingle only guarantees an object.
  const raw = coerceSingle(contents) as NineSetupCalendarReportDTO;
  return {
    from: toRequiredString(raw.from),
    to: toRequiredString(raw.to),
    status: toCalendarStatus(raw.status),
    totalSetups: toCount(raw.totalSetups),
    months: toList(raw.months).map(convertCalendarMonth),
  };
}

export default {
  convertNineSetup,
  convertNineSetupList,
  convertDeletedNineSetupId,
  convertCalendarData,
};
